<template>
  <v-dialog
    v-model="isOpen"
    max-width="500"
    aria-label="Import recipe from URL"
  >
    <v-card>
      <v-toolbar color="surface">
        <v-btn icon="mdi-close" dark @click="close" aria-label="close" />
        <v-toolbar-title>Import Recipe from URL</v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <v-text-field
          v-model="url"
          label="Recipe URL"
          placeholder="https://example.com/recipe"
          :error-messages="urlError"
          :disabled="loading"
          autofocus
        />
        <v-alert v-if="errorMessage" type="error" class="mt-2">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="loading">Cancel</v-btn>
        <v-btn
          color="primary"
          @click="importRecipe"
          :loading="loading"
          :disabled="loading"
        >
          Import
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
  import { defineComponent, type PropType, ref, watch } from 'vue';
  import type { Recipe, RecipePayload } from '@/service/recipe/types';
  import type RecipeClient from '@/service/recipe/recipeClient';
  import type { ClientInterface } from '@/service/types';

  export default defineComponent({
    name: 'ImportRecipeFromUrl',
    props: {
      client: {
        type: Object as PropType<ClientInterface<Recipe, RecipePayload>>,
        required: true,
      },
      open: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['imported', 'close'],
    setup(props, { emit }) {
      const isOpen = ref(props.open);
      const url = ref('');
      const urlError = ref('');
      const errorMessage = ref('');
      const loading = ref(false);

      watch(
        () => props.open,
        (val) => {
          isOpen.value = val;
          if (val) {
            url.value = '';
            urlError.value = '';
            errorMessage.value = '';
          }
        },
      );
      watch(isOpen, (val) => {
        if (!val) emit('close');
      });

      function isValidUrl(value: string): boolean {
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      }

      function close() {
        isOpen.value = false;
        url.value = '';
        urlError.value = '';
        errorMessage.value = '';
      }

      async function importRecipe() {
        urlError.value = '';
        errorMessage.value = '';

        if (!url.value.trim()) {
          urlError.value = 'Please enter a URL.';
          return;
        }
        if (!isValidUrl(url.value.trim())) {
          urlError.value = 'Please enter a valid URL.';
          return;
        }

        loading.value = true;
        try {
          const recipe = await (props.client as RecipeClient).importFromUrl(
            url.value.trim(),
          );
          emit('imported', recipe);
          close();
        } catch (err: unknown) {
          errorMessage.value =
            (err instanceof Error ? err.message : undefined) ??
            'Could not extract a recipe from this page. Please fill in the details manually.';
        } finally {
          loading.value = false;
        }
      }

      return {
        isOpen,
        url,
        urlError,
        errorMessage,
        loading,
        close,
        importRecipe,
      };
    },
  });
</script>
